// src/scenes/sitemanager/DashboardMPLH.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useTheme,
    TextField,
    Card,
    CardContent,
    Button,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
} from "recharts";
import StatBox from "../../component-files/StatBox";
import Header from "../../component-files/Header";
import axios from "axios";
import { tokens } from "../../theme";

/* ---------------- helpers ---------------- */
const pad2 = (n) => String(n).padStart(2, "0");
const normalizeYM = (ym) => String(ym).replace(/\s+/g, ""); // "2025- 08" -> "2025-08"
const today = new Date();
const curYM = `${today.getFullYear()}-${pad2(today.getMonth() + 1)}`;
const schoolYearStartYear =
    today.getMonth() >= 6 ? today.getFullYear() : today.getFullYear() - 1; // Jul=6
const schoolYearStart = `${schoolYearStartYear}-07`; // 1 July

const isWithinSYToDate = (ym) =>
    normalizeYM(ym) >= schoolYearStart && normalizeYM(ym) <= curYM;

const fmt2 = (v) => (v ?? 0).toFixed(2);

/* --------------- API --------------- */
const KPI_MPLH_URL =
    "https://figazuresql.swancreate.com/api/swa/vw_KPI_MealsPerLaborHourBySiteMonth";

export default function DashboardMPLH() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // filters
    const [siteFilter, setSiteFilter] = useState(""); // "" = All Sites
    const [monthFilter, setMonthFilter] = useState("");

    // data
    const [raw, setRaw] = useState([]); // all KPI rows (SY→current)
    const [sites, setSites] = useState([]); // [{id, name}]
    const [months, setMonths] = useState([]); // ["YYYY-MM"]

    // derived views
    const [leaderBoardData, setLeaderBoardData] = useState([]); // YTD by site
    const [chartData, setChartData] = useState([]); // same as leaderboard
    const [rows, setRows] = useState([]); // monthly details for selection
    const [ytd, setYtd] = useState({ meq: 0, hrs: 0, mplh: 0 });

    // keep old inputs visible (not used for KPI)
    const [conv, setConv] = useState({
        lunch: 1.0,
        supper: 1.0,
        breakfast: 0.67,
        snack: 0.33,
    });

    /* ---------- 1) Load KPI data (SY→current month only) ---------- */
    useEffect(() => {
        (async () => {
            const res = await axios.get(KPI_MPLH_URL);
            const kpi = (res.data || []).map((r) => ({
                SchoolID: r.SchoolID,
                SchoolName: r.SchoolName,
                YearMonth: normalizeYM(r.YearMonth),
                TotalMEQ: Number(r.TotalMEQ ?? 0),
                TotalLaborHours: Number(r.TotalLaborHours ?? 0),
                // API includes MPLH, but we will always recompute from totals to be safe
                MPLH: Number(r.MPLH ?? 0),
            }));

            // limit to current school-year up to current month
            const filtered = kpi.filter((r) => isWithinSYToDate(r.YearMonth));

            // sites (All Sites + alpha sort)
            const siteMap = new Map();
            filtered.forEach((r) => siteMap.set(r.SchoolID, r.SchoolName));
            const siteOptions = Array.from(siteMap, ([id, name]) => ({ id, name })).sort((a, b) =>
                a.name.localeCompare(b.name)
            );
            setSites([{ id: "", name: "All Sites" }, ...siteOptions]);

            // months from filtered rows
            const monthSet = new Set(filtered.map((r) => r.YearMonth));
            setMonths(Array.from(monthSet).sort());

            setRaw(filtered);
        })();
    }, []);

    /* ---------- 2) Leaderboard + chart (YTD ΣMEQ / ΣHrs per site) ---------- */
    useEffect(() => {
        if (!raw.length) return;

        // Optionally restrict to a single month if monthFilter is set
        const scoped = monthFilter ? raw.filter((r) => r.YearMonth === monthFilter) : raw;

        const agg = new Map(); // id -> {name, meq, hrs}
        scoped.forEach((r) => {
            const g = agg.get(r.SchoolID) || { siteId: r.SchoolID, siteName: r.SchoolName, meq: 0, hrs: 0 };
            g.meq += r.TotalMEQ;
            g.hrs += r.TotalLaborHours;
            agg.set(r.SchoolID, g);
        });

        const allSitesData = Array.from(agg.values())
            .map((g) => ({
                siteId: g.siteId,
                siteName: g.siteName,
                mplh: g.hrs > 0 ? +(g.meq / g.hrs).toFixed(2) : 0, // **correct aggregation**
            }))
            .sort((a, b) => b.mplh - a.mplh);

        setLeaderBoardData(allSitesData);
        setChartData(allSitesData);
    }, [raw, monthFilter]);

    /* ---------- 3) Monthly detail rows for current selection ---------- */
    useEffect(() => {
        if (!raw.length) return;

        // Filter by site (optional) and month (optional)
        const scoped = raw.filter((r) => {
            const siteOK = !siteFilter || r.SchoolID === siteFilter;
            const monthOK = !monthFilter || r.YearMonth === monthFilter;
            return siteOK && monthOK;
        });

        // Group by month and sum MEQ + Hours
        const byMonth = new Map(); // ym -> {meq, hrs}
        scoped.forEach((r) => {
            const g = byMonth.get(r.YearMonth) || { meq: 0, hrs: 0 };
            g.meq += r.TotalMEQ;
            g.hrs += r.TotalLaborHours;
            byMonth.set(r.YearMonth, g);
        });

        const detailRows = Array.from(byMonth.entries())
            .sort(([a], [b]) => (a < b ? -1 : 1))
            .map(([ym, g]) => ({
                id: ym,
                month: ym,
                totalMEQ: g.meq,
                totalHours: g.hrs,
                mplh: g.hrs > 0 ? +(g.meq / g.hrs).toFixed(2) : null,
            }));

        setRows(detailRows);

        // YTD stat for current selection
        const sumMEQ = detailRows.reduce((s, r) => s + (r.totalMEQ || 0), 0);
        const sumHrs = detailRows.reduce((s, r) => s + (r.totalHours || 0), 0);
        const mplh = sumHrs > 0 ? +(sumMEQ / sumHrs).toFixed(2) : 0;
        setYtd({ meq: sumMEQ, hrs: sumHrs, mplh });
    }, [raw, siteFilter, monthFilter]);

    /* ---------- rank ---------- */
    const currentRank = useMemo(() => {
        if (!siteFilter) return null;
        const idx = leaderBoardData.findIndex((d) => d.siteId === siteFilter);
        return idx >= 0 ? idx + 1 : null;
    }, [leaderBoardData, siteFilter]);
    const totalSites = leaderBoardData.length;

    /* ---------- columns ---------- */
    const columns = useMemo(
        () => [
            { field: "month", headerName: "Month", width: 120 },
            {
                field: "totalMEQ",
                headerName: "MEQs",
                type: "number",
                width: 120,
                valueFormatter: ({ value }) => fmt2(value),
            },
            {
                field: "totalHours",
                headerName: "Labor Hrs",
                type: "number",
                width: 120,
                valueFormatter: ({ value }) => fmt2(value),
            },
            {
                field: "mplh",
                headerName: "MPLH",
                type: "number",
                width: 110,
                valueFormatter: ({ value }) => (value != null ? fmt2(value) : "—"),
            },
        ],
        []
    );

    /* ---------- CSV ---------- */
    const downloadDetailCSV = () => {
        const siteName = !siteFilter ? "All Sites" : sites.find((s) => s.id === siteFilter)?.name || "";
        const header = ["Site", "Month", "TotalMEQ", "LaborHours", "MPLH"];
        const csv = [
            header.join(","),
            ...rows.map((r) =>
                [JSON.stringify(siteName), r.month, fmt2(r.totalMEQ), fmt2(r.totalHours), r.mplh != null ? fmt2(r.mplh) : ""].join(",")
            ),
        ].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "mplh_detail.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    const downloadLeaderboardCSV = () => {
        const header = ["Rank", "Site", "MPLH"];
        const csv = [
            header.join(","),
            ...leaderBoardData.map((d, i) => [i + 1, JSON.stringify(d.siteName), fmt2(d.mplh)].join(",")),
        ].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "mplh_leaderboard.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Box p={3}>
            <Header
                title="Site MPLH Dashboard"
                subtitle="MEQs per Labor Hour • School Year to Date (≤ current month)"
            />

            {/* Keep these fields visible; they don’t affect KPI math */}
            <Box display="flex" gap={2} mb={3}>
                {["lunch", "supper", "breakfast", "snack"].map((key) => (
                    <TextField
                        key={key}
                        label={`${key.charAt(0).toUpperCase() + key.slice(1)} MEQ`}
                        type="number"
                        size="small"
                        inputProps={{ step: 0.01, min: 0, readOnly: true }}
                        value={conv[key]}
                        onChange={(e) =>
                            setConv((c) => ({ ...c, [key]: parseFloat(e.target.value) || 0 }))
                        }
                    />
                ))}
            </Box>

            {/* Filters */}
            <Box display="flex" gap={2} mb={3}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel>Site</InputLabel>
                    <Select
                        value={siteFilter}
                        label="Site"
                        onChange={(e) => setSiteFilter(e.target.value)}
                    >
                        {sites.map((s) => (
                            <MenuItem key={s.id || "__all"} value={s.id}>
                                {s.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Month</InputLabel>
                    <Select
                        value={monthFilter}
                        label="Month"
                        onChange={(e) => setMonthFilter(e.target.value)}
                    >
                        <MenuItem value="">All Months</MenuItem>
                        {months.map((m) => (
                            <MenuItem key={m} value={m}>
                                {m}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* MPLH YTD stat (ΣMEQ ÷ ΣHrs for current selection) */}
            <StatBox
                title={fmt2(ytd.mplh)}
                subtitle={`${fmt2(ytd.meq)} MEQs ÷ ${fmt2(ytd.hrs)} hrs`}
                borderColor={colors.primary[500]}
            />

            {/* Chart + Rank + Leaderboard */}
            <Box display="flex" gap={2} mb={3} alignItems="flex-start">
                {/* bar chart */}
                <Box flex={2}>
                    <Typography variant="h6" gutterBottom>
                        District MPLH (YTD{monthFilter ? ` • ${monthFilter}` : ""})
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                            data={chartData}
                            margin={{ top: 5, right: 20, left: 20, bottom: 60 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="siteName" angle={-45} textAnchor="end" interval={0} />
                            <YAxis />
                            <RechartsTooltip />
                            <Bar dataKey="mplh" fill={colors.blueAccent[500]} />
                        </BarChart>
                    </ResponsiveContainer>
                </Box>

                {/* rank card (shown when a specific site is selected) */}
                {currentRank && (
                    <Box
                        sx={{
                            flex: 1,
                            p: 4,
                            borderRadius: 3,
                            textAlign: "center",
                            boxShadow: `0 0 20px ${colors.greenAccent[500]}`,
                            backgroundColor: "transparent",
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            {sites.find((s) => s.id === siteFilter)?.name}
                        </Typography>
                        <Typography variant="h1" fontWeight="bold" mb={1}>
                            {currentRank}
                        </Typography>
                        <Typography variant="subtitle1" gutterBottom>
                            rank out of {totalSites}
                        </Typography>
                        <Typography variant="h2" fontWeight="bold" mb={1}>
                            {fmt2(ytd.mplh)}
                        </Typography>
                        <Typography variant="subtitle2" color={colors.greenAccent[700]} gutterBottom>
                            MEQ per Labor Hour
                        </Typography>
                        <Typography variant="body2" color={colors.greenAccent[700]}>
                            Great progress!
                        </Typography>
                    </Box>
                )}

                {/* leaderboard */}
                <Box flex={1}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="h6">
                            🏆 Top Sites — MPLH (YTD{monthFilter ? ` • ${monthFilter}` : ""})
                        </Typography>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={downloadLeaderboardCSV}
                            sx={{
                                backgroundColor: theme.palette.success.main,
                                color: theme.palette.getContrastText(theme.palette.success.main),
                                "&:hover": { backgroundColor: theme.palette.success.dark },
                            }}
                        >
                            Download CSV
                        </Button>
                    </Box>
                    <Box
                        component="ol"
                        sx={{
                            pl: 2,
                            m: 0,
                            maxHeight: 320,
                            overflowY: "auto",
                            "& li": {
                                mb: 0.5,
                                borderRadius: 1,
                                px: 1,
                                py: 0.5,
                                display: "flex",
                                justifyContent: "space-between",
                            },
                            "& li:nth-of-type(odd)": {
                                backgroundColor: theme.palette.action.hover,
                            },
                        }}
                    >
                        {leaderBoardData.map(({ siteName, mplh }, i) => (
                            <Typography key={`${siteName}-${i}`} component="li">
                                <span>{`${i + 1}. ${siteName}`}</span>
                                <span>{fmt2(mplh)}</span>
                            </Typography>
                        ))}
                    </Box>
                </Box>
            </Box>

            {/* Monthly Detail */}
            <Card>
                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="h6">Monthly Detail</Typography>
                        <Button
                            variant="contained"
                            onClick={downloadDetailCSV}
                            sx={{
                                backgroundColor: theme.palette.success.main,
                                color: theme.palette.getContrastText(theme.palette.success.main),
                                "&:hover": { backgroundColor: theme.palette.success.dark },
                            }}
                        >
                            Download CSV
                        </Button>
                    </Box>
                    <Box sx={{ height: 500, width: "100%" }}>
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            pageSize={10}
                            rowsPerPageOptions={[10, 25, 50]}
                            pagination
                            disableSelectionOnClick
                            sx={{
                                "& .MuiDataGrid-row:nth-of-type(odd)": {
                                    backgroundColor: theme.palette.action.hover,
                                },
                                "& .MuiDataGrid-row:nth-of-type(even)": {
                                    backgroundColor: "transparent",
                                },
                            }}
                        />
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}
